import axios from 'axios';
import type { Optional } from '@gov-nx/core/types';
import type { AuthState, ConfigurationState, UserState } from '@gov-nx/store';
import { getStore, isLanguageSetting } from '@gov-nx/store';
import { getPoServerUrl } from '../../store/src/configuration/utils';
import { logger } from './logger';
import type { RezaOpravneniDto } from './generated';

const LANG_GET_URL = '/api/v1/be/nastaveni/LANG';
const LANG_POST_URL = '/api/v1/be/nastaveni';
const LANG_PAYLOAD_KEY = 'LANG';

const instance = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	paramsSerializer: {
		indexes: null,
	},
});

export const useAxiosInstance = () =>
	// TODO: Setup request interceptors in here

	({ axiosInstance: instance });

let activeSwitcherReza: RezaOpravneniDto | null = null;

export const setActiveSwitcherRezaForAxios = (
	reza: RezaOpravneniDto | null
) => {
	activeSwitcherReza = reza;
};

const setup = () => {
	instance.interceptors.request.use((config) => {
		const userStore = getStore<Optional<UserState>>('user')?.getState();
		const authStore = getStore<Optional<AuthState>>('auth')?.getState();
		const configurationStore =
			getStore<Optional<ConfigurationState>>('configuration')?.getState();

		const language = userStore?.settings.find(isLanguageSetting)?.value;
		if (language) {
			config.headers['Accept-Language'] = language;
		}

		if (authStore?.token) {
			config.headers.Authorization = 'Bearer ' + authStore.token;
		}

		const isLangGetRequest =
			config.url === LANG_GET_URL && config.method === 'get';

		const isLangPostRequest =
			config.url === LANG_POST_URL &&
			config.method === 'post' &&
			config.data?.klic === LANG_PAYLOAD_KEY;

		const isLanguageSettingsRequest = isLangGetRequest || isLangPostRequest;

		// Language preference is user-global, not REZA-scoped —
		// sending x-okz would scope the setting to a single REZA incorrectly.
		if (!isLanguageSettingsRequest) {
			config.headers['x-okz'] =
				config.headers['x-okz'] ??
				activeSwitcherReza?.kod ??
				authStore?.activeReza?.kod ??
				'';
		}

		logger('request', [
			authStore?.token
				? `${authStore.token.substring(0, 16)} ... ${authStore.token.substring(
						authStore.token.length - 16,
						authStore.token.length - 1
					)}`
				: 'NO-TOKEN',
			config.url,
			JSON.stringify(config.params),
		]);

		const serverUrl = getPoServerUrl(configurationStore?.configuration);
		if (serverUrl) {
			config.baseURL = serverUrl;
		}

		return config;
	});
};

/**
 *
 * @deprecated calling setup() should not be necessary
 */
export const setupApiAxiosInterceptors = setup;
